import { Check, Minus } from "@phosphor-icons/react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";

import type React from "react";
import type { HTMLAttributes } from "react";

const checkboxVariants = cva(
  [
    "group/checkbox relative shrink-0 appearance-none border border-black-400 transition-[border-color,background-color]",
    "group-hover/checkbox:border-black-950 data-[state=checked]:hover:border-black-700 data-[state=indeterminate]:hover:border-black-700",
    "data-[disabled]:border-black-300 data-[disabled]:data-[state=checked]:border-black-300 data-[disabled]:bg-black-200 data-[disabled]:hover:border-black-300",
    "data-[state=indeterminate]:border-black-950",
    "data-[state=checked]:border-black-950",
  ],
  {
    variants: {
      size: {
        sm: "size-3.5 rounded",
        md: "size-4 rounded-[5px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const checkboxIndicatorVariants = cva(
  [
    "flex size-full items-center justify-center bg-black-950 text-white transition-[background-color]",
    "data-[disabled]:bg-black-300 data-[disabled]:text-black-400 data-[disabled]:group-hover/checkbox:bg-black-300",
    "group-hover/checkbox:bg-black-700",
  ],
  {
    variants: {
      size: {
        sm: "rounded-[2px]",
        md: "rounded-[4px]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

type CheckboxProps = VariantProps<typeof checkboxVariants> &
  (
    | (Omit<CheckboxPrimitive.CheckboxProps, "type" | "value"> & {
        isStyleOnly?: never;
      })
    | (HTMLAttributes<HTMLDivElement> & {
        isStyleOnly?: true;
        checked: boolean | "indeterminate";
        disabled?: boolean;
      })
  );

export default function Checkbox(props: CheckboxProps) {
  const {
    size = "md",
    checked,
    className,
    isStyleOnly = false,
    ...rest
  } = props;
  const Icon = checked === "indeterminate" ? Minus : Check;

  const iconComponent = <Icon size={size === "md" ? 12 : 10} weight="bold" />;

  if (isStyleOnly === true) {
    return (
      <div
        {...(rest as HTMLAttributes<HTMLDivElement>)}
        className={clsx(
          checkboxVariants({ size }),
          checked &&
            "border-black-950 bg-black-950 text-white group-hover/checkbox:border-black-700 group-hover/checkbox:bg-black-700",
          className,
          "flex items-center justify-center",
        )}
      >
        {checked && iconComponent}
      </div>
    );
  }

  return (
    <CheckboxPrimitive.Root
      {...(rest as React.ComponentProps<typeof CheckboxPrimitive.Root>)}
      checked={checked}
      className={clsx(
        checkboxVariants({ size }),
        className,
        "hover:border-black-950 before:size-6 before:absolute relative before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2",
      )}
    >
      <CheckboxPrimitive.Indicator
        className={checkboxIndicatorVariants({ size })}
      >
        {iconComponent}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}
